import React, { useState } from 'react';
import { Settings, Clock, Calendar, Sun, GraduationCap, Tag, FileText, Eye } from 'lucide-react';
import { WorkingHoursForm } from '../components/settings/WorkingHoursForm';
import { SessionTypesManager } from '../components/settings/SessionTypesManager';
import { HolidaysManager } from '../components/settings/HolidaysManager';
import { AcademicYearTemplateForm } from '../components/settings/AcademicYearTemplateForm';
import { EventCategoriesSettings } from '../components/settings/EventCategoriesSettings';
import { ExamConfigurationSettings } from '../components/settings/ExamConfigurationSettings';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';

type SettingsTab = 'academic-year' | 'working-hours' | 'session-types' | 'holidays' | 'categories' | 'exams';

const TABS: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
  { id: 'academic-year', label: 'Année académique', icon: <GraduationCap size={18} /> },
  { id: 'working-hours', label: 'Horaires de travail', icon: <Clock size={18} /> },
  { id: 'session-types', label: 'Types de séance', icon: <Calendar size={18} /> },
  { id: 'holidays', label: 'Jours fériés', icon: <Sun size={18} /> },
  { id: 'categories', label: 'Catégories d\'événements', icon: <Tag size={18} /> },
  { id: 'exams', label: 'Examens et Rattrapage', icon: <FileText size={18} /> },
];

export const CalendarSettingsPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<SettingsTab>('academic-year');

  // Users must at least have view access to open the settings page at all.
  const canView = hasPermission(Permission.CALENDAR_SETTINGS_VIEW);
  // Check if user has permission to manage settings (not just view)
  const canManage = hasPermission(Permission.CALENDAR_SETTINGS_MANAGE);

  if (!canView) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-3 p-6 bg-amber-50 border border-amber-200 rounded-xl max-w-2xl">
          <Eye className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h2 className="font-semibold text-amber-900">Accès non autorisé</h2>
            <p className="text-sm text-amber-700 mt-1">
              Vous n'avez pas la permission de consulter les paramètres du calendrier.
              Contactez un administrateur si vous pensez que c'est une erreur.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'working-hours':
        return <WorkingHoursForm canManage={canManage} />;
      case 'session-types':
        return <SessionTypesManager canManage={canManage} />;
      case 'holidays':
        return <HolidaysManager canManage={canManage} />;
      case 'academic-year':
        return <AcademicYearTemplateForm canManage={canManage} />;
      case 'categories':
        return <EventCategoriesSettings canManage={canManage} />;
      case 'exams':
        return <ExamConfigurationSettings canManage={canManage} />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Settings className="text-primary-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Paramètres du Calendrier
            </h1>
            <p className="text-gray-500 mt-1">
              Configurez les horaires, types de séances et jours fériés
            </p>
            {!canManage && (
              <div className="flex items-center gap-1.5 mt-2 text-amber-600 text-sm">
                <Eye size={14} />
                <span>Mode consultation uniquement</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                  ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};
